import is from '@sindresorhus/is';
import { Fragment, type FunctionComponent } from 'react';

import type { SkillShort } from '~/shared/data-contract/skill';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent } from '~/ui/core/tile/Tile';

import { Backdrop, ShakeAnimation, useShakeOnClickOutside } from '../job-architecture-v2';
import { useLearningOpportunityQuery } from './api';
import { LearningOpportunityContent } from './components/LearningOpportunityContent';
import { LearningOpportunitySidepanelSkeleton } from './components/LearningOpportunitySidepanelSkeleton';
import type { SidepanelMode } from './form-state';
import { useFormDefaultValue } from './hooks/use-form-default-value';
import { useRegisterLearningOpportunityChatContext } from './hooks/use-learning-opportunity-chat-context';
import { useSidepanelMode } from './hooks/use-sidepanel-mode';
import { useSidepanelMutations } from './hooks/use-sidepanel-mutations';

export const LearningOpportunitySidepanel: FunctionComponent<{
    readonly mode: SidepanelMode;
    readonly onClose: () => void;
    readonly onSkillClick: (skillId: string, preferredLanguage?: string) => void;
    readonly onTrainedSkillsChange: (skills: readonly SkillShort[]) => void;
    readonly opportunityId?: string;
}> = ({ mode: initialMode, onClose, onSkillClick, onTrainedSkillsChange, opportunityId }) => {
    const { isShaking, shake } = useShakeOnClickOutside();
    const { data, isPending } = useLearningOpportunityQuery(opportunityId ?? '', {
        enabled: is.nonEmptyString(opportunityId) && initialMode !== 'create',
    });
    const close = (): void => {
        onClose();
    };
    const { effectiveMode, isCreate, isEditing, setMode } = useSidepanelMode(initialMode, opportunityId);
    useRegisterLearningOpportunityChatContext(opportunityId, effectiveMode);
    const defaultValue = useFormDefaultValue(initialMode, data);
    const { handlePublish, handleSubmit, isPublishing, isSubmitting } = useSidepanelMutations({
        close,
        data,
        isCreate,
        opportunityId,
        setMode,
    });
    const isLoading = !isCreate && (isPending || data === undefined);
    const learningOpportunity = isCreate ? undefined : data;
    const handleCancel = isCreate ? close : () => setMode('view');
    const handleEdit = isCreate ? undefined : () => setMode('edit');

    return (
        <Fragment>
            {isEditing && <Backdrop onClick={shake} />}
            <ShakeAnimation isShaking={isShaking}>
                <Tile>
                    <ScrollArea>
                        <TileContent data-testid="learning-opportunity-sidepanel">
                            {isLoading || defaultValue === undefined ? (
                                <LearningOpportunitySidepanelSkeleton />
                            ) : (
                                <LearningOpportunityContent
                                    defaultValue={defaultValue}
                                    isPublishing={isPublishing}
                                    isSubmitting={isSubmitting}
                                    learningOpportunity={learningOpportunity}
                                    mode={effectiveMode}
                                    onCancel={handleCancel}
                                    onClose={close}
                                    onEdit={handleEdit}
                                    onPublish={handlePublish}
                                    onSkillClick={onSkillClick}
                                    onSubmit={handleSubmit}
                                    onTrainedSkillsChange={onTrainedSkillsChange}
                                />
                            )}
                        </TileContent>
                    </ScrollArea>
                </Tile>
            </ShakeAnimation>
        </Fragment>
    );
};
